import { useMemo } from "react";

import { usePlayContext } from "../../../pages";
import { Action, MarkerPlayer } from "../../../types";
import { getActionsFromMovements, getAllBallMovementActions, getBallActions, getPlayerMarkers } from "../../../utils";
import { TIMELINE_VIEW } from "./use-animation-timer-api";

export interface PlayerWithAnimation extends MarkerPlayer {
  actions: Action[];
}

export interface FrameWithAnimation {
  playersWithAnimation: PlayerWithAnimation[];
  frameId: string;
}

export const useGetPlayersWithAnimation = (): FrameWithAnimation[] => {
  const { playStateApi, animationTimerApi } = usePlayContext();
  const { activeFrame, playState } = playStateApi;

  const { timelineView } = animationTimerApi;

  return useMemo(
    (): FrameWithAnimation[] =>
      playState.frames
        .filter((f) => timelineView === TIMELINE_VIEW.ALL || f.id === activeFrame?.id)
        .map(({ frameStencils, id: frameId }): FrameWithAnimation => {
          const passes = getBallActions(getAllBallMovementActions(frameStencils));

          const playersWithMovements = getPlayerMarkers(frameStencils)
            .filter(
              (player) => player.movements.length > 0 || passes.map((pass) => pass.prevPlayer).includes(player.id)
            )
            .sort((a, b) => {
              if (a.playerIdNumber && b.playerIdNumber) {
                return a.playerIdNumber - b.playerIdNumber;
              } else {
                return (a?.label || "O").localeCompare(b?.label || "O");
              }
            });

          return {
            frameId,
            playersWithAnimation: playersWithMovements.map(
              (player): PlayerWithAnimation => ({
                ...player,
                actions: [
                  ...getActionsFromMovements({ movements: player.movements, stencils: frameStencils }),
                  ...passes.filter((pass) => pass.prevPlayer === player.id),
                ],
              })
            ),
          };
        }),
    [playState, timelineView, activeFrame]
  );
};
